// Third-party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Dotdotdot from 'react-dotdotdot';

// Components
import DefaultModal from './DefaultModal';
import { CorrelationChart } from '../charts';
import { HoldingIcon } from '../common';
import CorrelationLegend from '../charts/correlation-chart/CorrelationLegend';
import { DateQuickRangeOneWay } from '../inputs';

// Utils
import { formatLocalizedString, formatMessage } from '../../../utils/i18n';
import { TIME_PERIODS, TIME_RANGE_PRESET } from '../../../utils/constants';
import { InfoTooltip } from '../tooltips';
import { getCorrelationDisclaimer } from '../../../utils/correlation-matrix';
import ComparisonItem from '@/types/comparison-item';
import RiskPeriods from '@/types/risk-periods';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import Nullable from '@/types/nullable';
import CorrelationChartVirtualized from '../charts/correlation-chart/CorrelationChartVirtualized';
import Comparison from '@/types/comparison';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { connect } from 'react-redux';
import { ReduxState } from '@/redux/modules/types';
import User from '@/types/user';
import { getCorrelationMatrix } from '@/utils/comparison/portfolio-comparison-item';
import { comparisonIsReadOnly } from '@/utils/comparison';

type StateProps = {
  user?: Nullable<User>;
};
type Props = {
  onCancel: () => void;
  item: Nullable<ComparisonItem>;
  comparison: Comparison;
} & StateProps;

type State = {
  riskPeriod: RiskPeriods;
  tooltipMessage: string;
};

const MAX_HOLDINGS_BEFORE_VIRTUALIZATION = 40;

class CorrelationModal extends React.Component<Props, State> {
  readonly state: State = {
    riskPeriod: TIME_PERIODS['3Y'],
    tooltipMessage: '',
  };

  onMonthsRangeChange = (dateQuickRange: TIME_PERIODS) => {
    this.setState({ riskPeriod: dateQuickRange as RiskPeriods });
  };

  onCorrelationPointHover = (message: string) => {
    this.setState({ tooltipMessage: message });
  };

  renderChild = (item: ComparisonItem) => {
    // Note on shortHistory VS useLongestHistory: shortHistory is a legacy term that is used in the comparison
    // but it really means that we want to use the longest history available for the item (settings in the comparison)
    // shortHistory was probably named this way to indicate that we would allow a holding of a portfolio to have shorter history than the rest
    const {
      comparison: { shortHistory: useLongestHistory, commonInception, showBaseCurrencies: useBaseCurrency },
      comparison,
    } = this.props;
    const { riskPeriod } = this.state;
    const { _id, performance, holdings, type, color, name } = item;

    const correlation =
      comparisonIsReadOnly(comparison) && performance?.correlation?.[riskPeriod]?.length
        ? performance.correlation[riskPeriod]
        : getCorrelationMatrix(item, riskPeriod, { useLongestHistory, commonInception, useBaseCurrency });
    const disclaimer = getCorrelationDisclaimer(item, riskPeriod, useLongestHistory);

    const isLargeMatrixNeedVirtualization = holdings.length > MAX_HOLDINGS_BEFORE_VIRTUALIZATION;

    return (
      <div className="chart-item" key={_id}>
        <div className="chart-title d-flex align-items-center">
          <HoldingIcon type={type} color={color} />

          <Dotdotdot clamp={2}>
            <span className="chart-header-name">{formatLocalizedString(name)}</span>
          </Dotdotdot>

          {disclaimer && <InfoTooltip {...disclaimer} />}
        </div>

        <div className="chart-body d-flex align-items-center">
          {correlation && !!correlation.length && <CorrelationLegend />}

          {isLargeMatrixNeedVirtualization ? (
            <CorrelationChartVirtualized holdings={holdings} correlation={correlation} onCorrelationPointHover={this.onCorrelationPointHover} />
          ) : (
            <CorrelationChart holdings={holdings} correlation={correlation} onClick={this.props.onCancel} />
          )}
        </div>
      </div>
    );
  };

  render() {
    // Note on shortHistory VS useLongestHistory: shortHistory is a legacy term that is used in the comparison
    // but it really means that we want to use the longest history available for the item (settings in the comparison)
    // shortHistory was probably named this way to indicate that we would allow a holding of a portfolio to have shorter history than the rest
    const {
      item,
      onCancel,
      comparison: { shortHistory: useLongestHistory, items },
      user,
    } = this.props;
    const { riskPeriod } = this.state;

    if (!item) {
      return null;
    }

    const body = (
      <>
        <div className="correlation-header">
          <DateQuickRangeOneWay
            selected={riskPeriod as TIME_PERIODS}
            onChange={this.onMonthsRangeChange}
            values={getDateQuickRangeFromPreset(
              TIME_RANGE_PRESET.PORTFOLIO_CORRELATION_MATRIX,
              useLongestHistory,
              userHasPermission(user, PERMISSIONS.COMMON_INCEPTION) && items.length > 1,
            )}
          />
          {this.state.tooltipMessage && <div className="correlation-tooltip-container">{this.state.tooltipMessage}</div>}
        </div>

        <PerfectScrollbar className="correlation-charts">{this.renderChild(item)}</PerfectScrollbar>
      </>
    );

    return (
      <div onClick={e => e.stopPropagation()}>
        <DefaultModal
          onCancel={onCancel}
          secondaryActionLabel={formatMessage('Common.Close')}
          onSecondaryAction={onCancel}
          className="correlation-modal"
          title={formatMessage('CorrelationModal.Title')}
          show={item != null}
          body={body}
          hidePrimaryButton
          hideCancelButton
        />
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {})(CorrelationModal);
